import {
  useEffect,
  useRef,
  useState,
} from "react";

import ChatboxPrompt from "./ChatboxPrompt";
import { PARALLAX } from "./constants";
import { HERO_IMAGES } from "./images";

const vertexShaderSource = `
attribute vec2 a_position;

void main() {
  gl_Position = vec4( a_position, 0, 1 );
}
`

const fragmentShaderSource = `
#ifdef GL_ES
  precision mediump float;
#endif

uniform vec4 resolution;
uniform vec2 parallaxOffset;
uniform vec2 threshold;
uniform float time;
uniform float pixelRatio;
uniform sampler2D image0;
uniform sampler2D image1;

vec2 mirrored(vec2 v) {
  vec2 m = mod(v,2.);
  return mix(m,2.0 - m, step(1.0 ,m));
}

void main() {
  // uvs and textures
  vec2 uv = pixelRatio*gl_FragCoord.xy / resolution.xy ;
  vec2 vUv = (uv - vec2(0.5))*resolution.zw + vec2(0.5);
  vUv.y = 1. - vUv.y;
  vec4 tex1 = texture2D(image1,mirrored(vUv));
  // Parallax effect - accentuated intensity (1.5x multiplier)
  float intensity = 1.5;
  vec2 fake3d = vec2(vUv.x + (tex1.r - 0.5)*parallaxOffset.x/threshold.x*intensity, vUv.y + (tex1.r - 0.5)*parallaxOffset.y/threshold.y*intensity);
  vec4 tex0 = texture2D(image0,mirrored(fake3d));

  gl_FragColor = tex0;
}
`


interface HeroSectionProps {
  horizontalThreshold?: number
  verticalThreshold?: number
}

interface CanvasInstance {
  canvas: HTMLCanvasElement
  gl: WebGLRenderingContext
  program: WebGLProgram
  textures: WebGLTexture[]
  animationFrame: number | null
  startTime: number
  imageAspect: number
  offsetX: number
  offsetY: number
  targetOffsetX: number
  targetOffsetY: number
  uResolutionLoc: WebGLUniformLocation | null
  uParallaxOffsetLoc: WebGLUniformLocation | null
  uTimeLoc: WebGLUniformLocation | null
  uRatioLoc: WebGLUniformLocation | null
  uThresholdLoc: WebGLUniformLocation | null
  uImage0Loc: WebGLUniformLocation | null
  uImage1Loc: WebGLUniformLocation | null
  opacity: number
  isCleaningUp: boolean
}

function createCanvasInstance(container: HTMLDivElement): CanvasInstance | null {
  const canvas = document.createElement('canvas')
  canvas.style.position = 'absolute'
  canvas.style.top = '0'
  canvas.style.left = '0'
  canvas.style.width = '100%'
  canvas.style.height = '100%'
  canvas.style.zIndex = '1'
  canvas.style.opacity = '0'
  canvas.style.transition = 'opacity 1.5s ease-in-out'
  container.appendChild(canvas)

  const gl = canvas.getContext('webgl')
  if (!gl) {
    console.error('WebGL not supported')
    return null
  }

  // Set clear color to #23201b
  gl.clearColor(0.137, 0.125, 0.106, 1)
  gl.clear(gl.COLOR_BUFFER_BIT)

  function createShader(gl: WebGLRenderingContext, type: number, source: string): WebGLShader | null {
    const shader = gl.createShader(type)
    if (!shader) return null
    gl.shaderSource(shader, source)
    gl.compileShader(shader)
    if (!gl.getShaderParameter(shader, gl.COMPILE_STATUS)) {
      console.error('Shader compile error:', gl.getShaderInfoLog(shader))
      gl.deleteShader(shader)
      return null
    }
    return shader
  }

  const vertexShader = createShader(gl, gl.VERTEX_SHADER, vertexShaderSource)
  const fragmentShader = createShader(gl, gl.FRAGMENT_SHADER, fragmentShaderSource)
  if (!vertexShader || !fragmentShader) return null

  const program = gl.createProgram()
  if (!program) return null
  gl.attachShader(program, vertexShader)
  gl.attachShader(program, fragmentShader)
  gl.linkProgram(program)
  if (!gl.getProgramParameter(program, gl.LINK_STATUS)) {
    console.error('Program link error:', gl.getProgramInfoLog(program))
    return null
  }
  gl.useProgram(program)

  const positionBuffer = gl.createBuffer()
  gl.bindBuffer(gl.ARRAY_BUFFER, positionBuffer)
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 1, -1, -1, 1, 1, 1]), gl.STATIC_DRAW)
  const positionLocation = gl.getAttribLocation(program, 'a_position')
  gl.enableVertexAttribArray(positionLocation)
  gl.vertexAttribPointer(positionLocation, 2, gl.FLOAT, false, 0, 0)

  return {
    canvas,
    gl,
    program,
    textures: [],
    animationFrame: null,
    startTime: Date.now(),
    imageAspect: 1,
    offsetX: 0,
    offsetY: 0,
    targetOffsetX: 0,
    targetOffsetY: 0,
    uResolutionLoc: gl.getUniformLocation(program, 'resolution'),
    uParallaxOffsetLoc: gl.getUniformLocation(program, 'parallaxOffset'),
    uTimeLoc: gl.getUniformLocation(program, 'time'),
    uRatioLoc: gl.getUniformLocation(program, 'pixelRatio'),
    uThresholdLoc: gl.getUniformLocation(program, 'threshold'),
    uImage0Loc: gl.getUniformLocation(program, 'image0'),
    uImage1Loc: gl.getUniformLocation(program, 'image1'),
    opacity: 0,
    isCleaningUp: false,
  }
}

function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.crossOrigin = "anonymous"
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

function setupCanvasTextures(
  instance: CanvasInstance,
  images: HTMLImageElement[],
  horizontalThreshold: number,
  verticalThreshold: number
) {
  if (instance.isCleaningUp) return

  const { gl } = instance
  instance.imageAspect = images[0].naturalHeight / images[0].naturalWidth

  // Make sure program is active
  gl.useProgram(instance.program)

  // Clean up old textures
  instance.textures.forEach((texture) => gl.deleteTexture(texture))
  instance.textures = []

  // Create new textures
  images.forEach((img) => {
    const texture = gl.createTexture()
    if (!texture) return
    gl.bindTexture(gl.TEXTURE_2D, texture)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR)
    gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR)
    gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, img)
    instance.textures.push(texture)
  })

  if (instance.uImage0Loc) gl.uniform1i(instance.uImage0Loc, 0)
  if (instance.uImage1Loc) gl.uniform1i(instance.uImage1Loc, 1)

  gl.activeTexture(gl.TEXTURE0)
  gl.bindTexture(gl.TEXTURE_2D, instance.textures[0])
  gl.activeTexture(gl.TEXTURE1)
  gl.bindTexture(gl.TEXTURE_2D, instance.textures[1])

  // Update threshold
  if (instance.uThresholdLoc) {
    gl.uniform2f(instance.uThresholdLoc, horizontalThreshold, verticalThreshold)
  }
}

function resizeCanvas(instance: CanvasInstance, container: HTMLDivElement) {
  if (instance.isCleaningUp) return

  const { canvas, gl } = instance
  const ratio = window.devicePixelRatio || 1
  const width = container.offsetWidth
  const height = container.offsetHeight

  canvas.width = width * ratio
  canvas.height = height * ratio
  canvas.style.width = width + 'px'
  canvas.style.height = height + 'px'

  // Make sure program is active
  gl.useProgram(instance.program)

  let a1: number, a2: number
  if (height / width < instance.imageAspect) {
    a1 = 1
    a2 = (height / width) / instance.imageAspect
  } else {
    a1 = (width / height) * instance.imageAspect
    a2 = 1
  }

  if (instance.uResolutionLoc) {
    gl.uniform4f(instance.uResolutionLoc, width, height, a1, a2)
  }
  if (instance.uRatioLoc) {
    gl.uniform1f(instance.uRatioLoc, 1 / ratio)
  }
  gl.viewport(0, 0, width * ratio, height * ratio)
}

function startRenderLoop(instance: CanvasInstance) {
  const { gl } = instance

  const render = () => {
    // Stop rendering if cleanup has started
    if (instance.isCleaningUp) {
      return
    }

    // Make sure program is active
    gl.useProgram(instance.program)

    // Ensure we have textures before rendering
    if (instance.textures.length < 2) {
      instance.animationFrame = requestAnimationFrame(render)
      return
    }

    const currentTime = (Date.now() - instance.startTime) / 1000
    if (instance.uTimeLoc) {
      gl.uniform1f(instance.uTimeLoc, currentTime)
    }

    // Auto-panning: continuous oscillation left to right
    instance.targetOffsetX = Math.sin(currentTime * PARALLAX.X_SPEED) * PARALLAX.X_AMPLITUDE
    instance.targetOffsetY = Math.sin(currentTime * PARALLAX.Y_SPEED + 0.5) * PARALLAX.Y_AMPLITUDE

    // Smooth movement interpolation
    instance.offsetX += (instance.targetOffsetX - instance.offsetX) * PARALLAX.SMOOTHING
    instance.offsetY += (instance.targetOffsetY - instance.offsetY) * PARALLAX.SMOOTHING

    if (instance.uParallaxOffsetLoc) {
      gl.uniform2f(instance.uParallaxOffsetLoc, instance.offsetX, instance.offsetY)
    }

    // Clear canvas with #23201b
    gl.clearColor(0.137, 0.125, 0.106, 1)
    gl.clear(gl.COLOR_BUFFER_BIT)

    // Ensure textures are bound
    gl.activeTexture(gl.TEXTURE0)
    gl.bindTexture(gl.TEXTURE_2D, instance.textures[0])
    gl.activeTexture(gl.TEXTURE1)
    gl.bindTexture(gl.TEXTURE_2D, instance.textures[1])

    gl.drawArrays(gl.TRIANGLE_STRIP, 0, 4)
    instance.animationFrame = requestAnimationFrame(render)
  }
  render()
}

export default function HeroSection({
  horizontalThreshold = 100,
  verticalThreshold = 100,
}: HeroSectionProps) {
  const containerRef = useRef<HTMLDivElement>(null)
  const instancesRef = useRef<CanvasInstance[]>([])
  const resizeHandlerRef = useRef<(() => void) | null>(null)
  const cycleTimerRef = useRef<NodeJS.Timeout | null>(null)
  const currentImageIndexRef = useRef(0)
  const [isLoaded, setIsLoaded] = useState(false)

  useEffect(() => {
    if (!containerRef.current) return

    let isMounted = true
    const container = containerRef.current

    // Create canvas instances for all 4 images
    const instances: CanvasInstance[] = []
    for (let i = 0; i < HERO_IMAGES.length; i++) {
      const instance = createCanvasInstance(container)
      if (!instance) continue

      instance.opacity = i === 0 ? 1 : 0
      instance.canvas.style.opacity = i === 0 ? '1' : '0'
      instance.canvas.style.transition = `opacity ${PARALLAX.FADE_DURATION_MS / 1000}s ease-in-out`

      instances.push(instance)
    }

    if (instances.length === 0) return
    instancesRef.current = instances

    // Resize handler
    const handleResize = () => {
      instancesRef.current.forEach((instance) => {
        resizeCanvas(instance, container)
      })
    }
    resizeHandlerRef.current = handleResize
    window.addEventListener('resize', handleResize)

    // Load all images
    const loadPromises = HERO_IMAGES.map((imagePair) =>
      Promise.all([loadImage(imagePair.original), loadImage(imagePair.depth)])
    )

    Promise.all(loadPromises)
      .then((allImages) => {
        // Guard against state updates after unmount
        if (!isMounted) return

        allImages.forEach((images, index) => {
          const instance = instances[index]
          if (!instance) return

          setupCanvasTextures(instance, images, horizontalThreshold, verticalThreshold)
          resizeCanvas(instance, container)
          startRenderLoop(instance)
        })

        // Fade in first canvas
        requestAnimationFrame(() => {
          if (!isMounted) return
          if (instances[0]) {
            instances[0].canvas.style.opacity = '1'
            instances[0].opacity = 1
          }
          // Trigger text animations after canvas is visible
          setTimeout(() => {
            if (isMounted) {
              setIsLoaded(true)
            }
          }, 300)
        })
      })
      .catch((error) => {
        console.error('Error loading images:', error)
        // Still trigger animations even if images fail
        setTimeout(() => {
          if (isMounted) {
            setIsLoaded(true)
          }
        }, 300)
      })

    // Image cycling timer
    const cycleImages = () => {
      if (!isMounted) return

      const prevIndex = currentImageIndexRef.current
      const nextIndex = (prevIndex + 1) % instances.length
      currentImageIndexRef.current = nextIndex

      // Fade out current
      if (instances[prevIndex]) {
        instances[prevIndex].canvas.style.opacity = '0'
        instances[prevIndex].opacity = 0
      }

      // Fade in next
      setTimeout(() => {
        if (!isMounted) return
        if (instances[nextIndex]) {
          instances[nextIndex].canvas.style.opacity = '1'
          instances[nextIndex].opacity = 1
        }
      }, 100)
    }

    cycleTimerRef.current = setInterval(cycleImages, PARALLAX.IMAGE_CYCLE_MS)

    // Visibility-based animation pausing
    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Pause all animations when tab is hidden
        instances.forEach((instance) => {
          if (instance.animationFrame !== null) {
            cancelAnimationFrame(instance.animationFrame)
            instance.animationFrame = null
          }
        })
        if (cycleTimerRef.current) {
          clearInterval(cycleTimerRef.current)
          cycleTimerRef.current = null
        }
      } else {
        // Resume animations when tab becomes visible
        instances.forEach((instance) => {
          if (!instance.isCleaningUp && instance.animationFrame === null) {
            startRenderLoop(instance)
          }
        })
        if (!cycleTimerRef.current) {
          cycleTimerRef.current = setInterval(cycleImages, PARALLAX.IMAGE_CYCLE_MS)
        }
      }
    }
    document.addEventListener('visibilitychange', handleVisibilityChange)

    return () => {
      isMounted = false

      document.removeEventListener('visibilitychange', handleVisibilityChange)
      if (resizeHandlerRef.current) {
        window.removeEventListener('resize', resizeHandlerRef.current)
      }
      if (cycleTimerRef.current) {
        clearInterval(cycleTimerRef.current)
      }

      // Clean up all instances
      instancesRef.current.forEach((instance) => {
        // Signal cleanup to stop render loop
        instance.isCleaningUp = true

        if (instance.animationFrame !== null) {
          cancelAnimationFrame(instance.animationFrame)
        }
        instance.textures.forEach((texture) => {
          instance.gl.deleteTexture(texture)
        })
        instance.gl.deleteProgram(instance.program)
        if (container.contains(instance.canvas)) {
          container.removeChild(instance.canvas)
        }
      })
      instancesRef.current = []
    }
  }, [horizontalThreshold, verticalThreshold])

  return (
    <div
      ref={containerRef}
      className="w-full h-screen relative flex-shrink-0"
      style={{ width: '100%', height: '100vh', backgroundColor: '#23201b' }}
    >
      <div className="absolute inset-0 z-10 flex flex-col items-center justify-center pointer-events-none">
        <div className="flex flex-col items-center justify-center mb-16">
          <h1
            className="text-white text-9xl md:text-[12rem] font-bold tracking-[0.2em] transition-all duration-1000 ease-out"
            style={{
              fontFamily: 'var(--font-pioneer), sans-serif',
              textShadow: '0 4px 20px rgba(0, 0, 0, 0.5)',
              opacity: isLoaded ? 0.9 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
            }}
          >
            YUNACABA
          </h1>
          <p
            className="text-white text-2xl md:text-4xl mt-4 font-light tracking-wide transition-all duration-1000 ease-out delay-300"
            style={{
              opacity: isLoaded ? 0.9 : 0,
              transform: isLoaded ? 'translateY(0)' : 'translateY(20px)',
              textShadow: '0 3px 16px rgba(0, 0, 0, 0.9)',
              fontFamily: 'var(--font-alga), sans-serif',
              letterSpacing: '0.05em', // wide tracking
            }}
          >
            Hello, world!
          </p>
        </div>
        <div
          className="w-full pointer-events-auto transition-all duration-1000 ease-out delay-500"
          style={{
            opacity: isLoaded ? 1 : 0,
            transform: isLoaded ? 'translateY(0)' : 'translateY(40px)',
          }}
        >
          <ChatboxPrompt />
        </div>
      </div>
    </div>
  )
}

